import { throttle } from 'lodash';
import React from 'react';
import { useLocation } from 'react-router-dom';

import appConfig from '../../appConfig';
import { eventsRootEl, USER_ACTIVITY_HEARTHBEAT } from '../../utils/eventDispatchers';

const inactivityTimeout = appConfig.portal.inactivityTimeout;
const activityCheckInterval = 5000;

/**
 * This hook will run a callback after not detecting any activity for the specified amount of seconds.
 * Activity is detected when the user scrolls the page, when the router's location changes or when the returned
 * invariant callback registerActivity is called
 *
 * @param {function} onTimeout Callback to execute after the timeout
 * @param {boolean} [enabled = true] optional parameter to control if the hook is enabled or not
 *
 * @returns Invariant throttled function to call to report activity and reset the timeout
 *
 */

export const useInactivityEffect = (onTimeout: () => void, enabled = true) => {
	const location = useLocation();
	const disabled = !inactivityTimeout || !enabled;

	const registerActivity = React.useMemo(
		() =>
			throttle(() => {
				// dont register activity if past inactivityTimeout
				if (getSecondsSinceLastHearthbeat() > inactivityTimeout) return;
				// TODO: delete this log
				// console.log('register activity');

				storeHeartbeat();
			}, activityCheckInterval),
		[]
	);

	// Setting up interval that checks if the inactivity time is past due every 5 sec
	React.useEffect(() => {
		if (disabled) return;

		const intervalFunction = () => {
			// TODO: delete this log
			// console.log(`expire if ${getSecondsSinceLastHearthbeat()} > ${inactivityTimeout}`);

			if (getSecondsSinceLastHearthbeat() > inactivityTimeout) {
				clearHearthbeat();
				clearInterval(intervalRef);
				onTimeout();
			}
		};

		const intervalRef = setInterval(intervalFunction, activityCheckInterval);

		intervalFunction();
		return () => {
			clearInterval(intervalRef);
		};
	}, [disabled, onTimeout]);

	// this effect can catch only location changes on the portal navigator, navigation on client apps (like wagering will not be detected).
	React.useEffect(() => {
		if (disabled) {
			clearHearthbeat();
			return;
		}

		registerActivity();
	}, [location, disabled, registerActivity]);

	// setting up a scroll event listener and a custom event fired fo activity via client apps
	React.useEffect(() => {
		if (disabled) return;

		document.addEventListener('scroll', registerActivity);
		eventsRootEl?.addEventListener(USER_ACTIVITY_HEARTHBEAT, registerActivity);

		return () => {
			document.removeEventListener('scroll', registerActivity);
			document.removeEventListener(USER_ACTIVITY_HEARTHBEAT, registerActivity);
		};
	}, [disabled, registerActivity]);

	return registerActivity;
};

// NOTE: if no heartbeat was found it will return 0
const getSecondsSinceLastHearthbeat = () => {
	const storedHeartBeat = window.localStorage.getItem(USER_ACTIVITY_HEARTHBEAT);

	if (!storedHeartBeat) return 0;

	const lastHearthbeat = parseInt(storedHeartBeat, 10);

	return (new Date().getTime() - lastHearthbeat) / 1000;
};

const storeHeartbeat = () => {
	window.localStorage.setItem(USER_ACTIVITY_HEARTHBEAT, new Date().getTime().toString());
};

const clearHearthbeat = () => {
	window.localStorage.removeItem(USER_ACTIVITY_HEARTHBEAT);
};
